import { useAppSelector } from "./redux";
import { CIP_2_0_UI_FEATURE } from "../util/betaFeatures";
import { isCompassusMode } from "../util/use2026UI";
import { isInternalLaunchOverride } from "../util/subdomain";

/**
 * Reactive reader for whether the app should render the 2026 ("2.0") UI.
 *
 * Enabled when EITHER:
 *   - BOTH the current user and the selected clinic have the `cip2.0-ui` beta
 *     feature (the intersection), OR
 *   - the internal launch override applies: an internal Circadia employee
 *     (`@circadia.health` non-`pcc_`) on a beta/local host (see
 *     {@link isInternalLaunchOverride}) — but ONLY once this session's clinic
 *     context has resolved (see `clinicContextResolved` below).
 *
 * Always returns `false` in Compassus mode, regardless of either condition.
 * Re-renders when any input (beta lists, selected clinic, user) changes.
 *
 * Why gate the override on clinic resolution: during bootstrap the user's email
 * loads (via `getAuthState`) before `populateAppData` resolves the clinic. With
 * no clinic yet, the Compassus check can't fire, so an internal employee would
 * briefly flip this `true` — and the router would enter onboarding — even if the
 * target clinic is Compassus/HH-RPM. Waiting for `userClinic` (persisted, so it
 * rehydrates instantly on repeat logins) or the clinic-beta-features-loaded flag
 * (the no-clinic case) keeps the Compassus kill-switch authoritative first.
 */
export const useUse2026UI = (): boolean => {
  const userHasFeature = useAppSelector((state) =>
    (state.auth.betaFeaturesAllowedList ?? []).includes(CIP_2_0_UI_FEATURE)
  );
  const clinicHasFeature = useAppSelector((state) =>
    (state.auth.clinicBetaFeaturesAllowedList ?? []).includes(
      CIP_2_0_UI_FEATURE
    )
  );
  const clinicType = useAppSelector(
    (state) => state.clinic?.userClinic?.clinic_type ?? null
  );
  const isHomeHealthRPM = useAppSelector(
    (state) => state.clinic?.userClinic?.config?.isHomeHealthRPM === true
  );
  const email = useAppSelector((state) => state.auth.user?.email ?? null);
  const clinicContextResolved = useAppSelector(
    (state) =>
      !!state.clinic?.userClinic || state.auth.clinicBetaFeaturesLoaded === true
  );

  if (isCompassusMode({ clinicType, isHomeHealthRPM })) {
    return false;
  }
  return (
    (userHasFeature && clinicHasFeature) ||
    (clinicContextResolved && isInternalLaunchOverride(email))
  );
};
